/**
 * insightsSignals.js — pure computation helpers that turn the raw data
 * we already load (transactions + goals + recurring) into the signals
 * that Insights and Reflections used to fake with fixtures.
 *
 * All functions are deterministic + side-effect free so they're trivial
 * to unit-test later. Each function is graceful with sparse data (falls
 * back to a reasonable "not enough history" state instead of throwing).
 *
 * Kept in /lib rather than /components/insights because Reflections uses
 * the same helpers.
 *
 * ---
 * Phase 6C update:
 *   • All helpers now normalize on the authoritative `txn_date` column
 *     (with legacy `date` alias tolerated for older callers/tests).
 *   • Clarity Score has moved to lib/clarityScore.js — this file
 *     re-exports it for backwards compatibility. The old 4-dimension
 *     average has been retired; the Clarity Score is now a proper
 *     client-side port of backend compute_metrics().
 */

// Re-export the new authoritative Clarity Score so old imports keep working.
export { computeClarityScore } from './clarityScore';

// ─── helpers ─────────────────────────────────────────────────────────
export function monthKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
export function monthLabel(k) {
  const [y, m] = k.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'long' });
}
function monthShortLabel(k) {
  const [y, m] = k.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}
function clamp01(v) { return Math.max(0, Math.min(100, v)); }

// Phase 6C — normalize on the authoritative `txn_date` column; tolerate
// legacy `date` alias so existing test data still parses.
function dateOf(t) { return t?.txn_date || t?.date || null; }

function rollupByMonth(txns) {
  const m = new Map();
  txns.forEach((t) => {
    const d = dateOf(t);
    if (!d) return;
    const k = monthKey(d);
    if (!m.has(k)) m.set(k, { key: k, income: 0, spending: 0, count: 0, categorized: 0 });
    const row = m.get(k);
    const amt = Math.abs(Number(t.amount || 0));
    if (t.type === 'income') row.income += amt;
    else if (t.type === 'expense') row.spending += amt;
    row.count += 1;
    if (t.category) row.categorized += 1;
  });
  return Array.from(m.values()).sort((a, b) => a.key.localeCompare(b.key));
}

// ─── 1. Patterns detector ────────────────────────────────────────────
/**
 * Returns up to ~6 human-readable patterns based on 6+ months of txns.
 * Categories used to trigger patterns:
 *   • Subscription trend
 *   • Positive cash-flow streak
 *   • Category with biggest MoM decrease (last 3m avg vs prior 3m avg)
 *   • Category with biggest MoM increase
 *   • Weekend dining share (if dining txns exist)
 */
export function detectPatterns({ txns = [], baseCurrency = 'INR' }) {
  const out = [];
  const roll = rollupByMonth(txns);
  if (roll.length < 3) {
    return [{
      id: 'not-enough',
      accent: '#9B9890', bg: '#F2F1EE',
      title: 'Not enough history to detect patterns yet.',
      detail: 'Once you have 3+ months of transactions, 2120 will surface consistent patterns worth knowing.',
      action: null,
    }];
  }

  // Positive cash flow streak
  const streak = [...roll].reverse().findIndex((r) => (r.income - r.spending) <= 0);
  const consecutive = streak === -1 ? roll.length : streak;
  if (consecutive >= 3) {
    const avgSurplus = roll.slice(-consecutive).reduce((s, r) => s + Math.max(0, r.income - r.spending), 0) / consecutive;
    out.push({
      id: 'streak',
      accent: '#3D7A63', bg: '#EEF4F0',
      title: `Positive cash flow for ${consecutive} consecutive month${consecutive === 1 ? '' : 's'}.`,
      detail: `You have kept more than you spent every month in that window. Your average monthly surplus is ${_fmt(avgSurplus, baseCurrency)}.`,
      action: null,
    });
  }

  // Category diffs — last 3 months vs prior 3
  if (roll.length >= 6) {
    const last3keys = roll.slice(-3).map((r) => r.key);
    const prev3keys = roll.slice(-6, -3).map((r) => r.key);
    const buckets = (keys) => {
      const b = {};
      txns.filter((t) => {
        const d = dateOf(t);
        return t.type === 'expense' && d && keys.includes(monthKey(d));
      }).forEach((t) => {
        const c = t.category || 'Miscellaneous';
        b[c] = (b[c] || 0) + Math.abs(Number(t.amount) || 0);
      });
      return b;
    };
    const cur = buckets(last3keys);
    const prev = buckets(prev3keys);
    const diffs = Object.keys(cur).map((c) => {
      const p = prev[c] || 0;
      const change = p > 0 ? ((cur[c] - p) / p) * 100 : 0;
      return { category: c, cur: cur[c], prev: p, change, abs: Math.abs(cur[c] - p) };
    }).filter((d) => d.prev > 100 && Math.abs(d.change) >= 5);
    diffs.sort((a, b) => b.abs - a.abs);
    const biggestDown = diffs.filter((d) => d.change < 0)[0];
    const biggestUp   = diffs.filter((d) => d.change > 0)[0];
    if (biggestDown) {
      out.push({
        id: 'down-' + biggestDown.category,
        accent: '#3D7A63', bg: '#EEF4F0',
        title: `${biggestDown.category} spending is down ${Math.abs(biggestDown.change).toFixed(1)}% over 3 months`,
        detail: `You spent ${_fmt(biggestDown.abs, baseCurrency)} less on ${biggestDown.category} in the last quarter compared to the previous one.`,
        action: null,
      });
    }
    if (biggestUp) {
      out.push({
        id: 'up-' + biggestUp.category,
        accent: '#A84E38', bg: '#FAF1EE',
        title: `${biggestUp.category} spending is up ${biggestUp.change.toFixed(1)}% over 3 months`,
        detail: `You spent ${_fmt(biggestUp.abs, baseCurrency)} more on ${biggestUp.category} in the last quarter compared to the previous one.`,
        action: `Review ${biggestUp.category}`,
      });
    }
  }

  // Weekend dining share
  const diningKeys = ['Food & Dining', 'Restaurants', 'Dining', 'Eating out'];
  const dining = txns.filter((t) => t.type === 'expense' && diningKeys.some((k) => (t.category || '').toLowerCase().includes(k.toLowerCase().slice(0, 6))));
  if (dining.length >= 20) {
    const weekend = dining.filter((t) => {
      const d = dateOf(t);
      if (!d) return false;
      const day = new Date(d).getDay();
      return day === 0 || day === 6;
    });
    const total = dining.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
    const weekendTotal = weekend.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
    const share = total > 0 ? (weekendTotal / total) * 100 : 0;
    if (share >= 50) {
      out.push({
        id: 'weekend-dining',
        accent: '#3E6A80', bg: '#EEF2F5',
        title: 'Dining out peaks on weekends',
        detail: `Saturday and Sunday account for ${share.toFixed(0)}% of your restaurant spending.`,
        action: null,
      });
    }
  }

  // Subscriptions trend
  const subs = txns.filter((t) => t.type === 'expense' && /subscription|netflix|prime|spotify|hotstar|disney|apple|google.*one/i.test(t.description || ''));
  if (subs.length >= 4) {
    const byMonth = {};
    subs.forEach((t) => {
      const d = dateOf(t);
      if (!d) return;
      const k = monthKey(d);
      byMonth[k] = (byMonth[k] || 0) + Math.abs(Number(t.amount) || 0);
    });
    const months = Object.keys(byMonth).sort();
    if (months.length >= 4) {
      const half = Math.floor(months.length / 2);
      const early = months.slice(0, half).reduce((s, k) => s + byMonth[k], 0) / half;
      const late  = months.slice(half).reduce((s, k) => s + byMonth[k], 0) / (months.length - half);
      if (early > 0) {
        const pct = ((late - early) / early) * 100;
        if (Math.abs(pct) >= 8) {
          out.push({
            id: 'subs',
            accent: pct > 0 ? '#3E6A80' : '#3D7A63',
            bg:     pct > 0 ? '#EEF2F5' : '#EEF4F0',
            title: `Subscriptions ${pct > 0 ? 'up' : 'down'} ${Math.abs(pct).toFixed(1)}% over recent months`,
            detail: `Recurring subscription-like charges have shifted from ~${_fmt(early, baseCurrency)}/mo to ~${_fmt(late, baseCurrency)}/mo.`,
            action: pct > 0 ? 'Review subscriptions' : null,
          });
        }
      }
    }
  }

  if (out.length === 0) {
    out.push({
      id: 'stable',
      accent: '#9B9890', bg: '#F2F1EE',
      title: 'Your patterns look steady this quarter.',
      detail: 'No unusual increases or decreases across the categories we watch.',
      action: null,
    });
  }
  return out.slice(0, 6);
}

function _fmt(amount, ccy) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(amount);
  } catch { return String(Math.round(amount)); }
}

// ─── 2. Monthly recaps ───────────────────────────────────────────────
export function computeMonthlyRecaps({ txns = [], goals = [], recurring = [], baseCurrency = 'INR' }) {
  // goals/recurring are accepted for API symmetry with the caller but only
  // txns are used for the recap headlines below. Ignore lint/unused warnings.
  void goals; void recurring;

  const roll = rollupByMonth(txns);
  if (roll.length < 2) return [];

  // Build category maps per month for highlights
  const byMonthByCat = new Map();
  txns.forEach((t) => {
    const d = dateOf(t);
    if (!d) return;
    const k = monthKey(d);
    if (!byMonthByCat.has(k)) byMonthByCat.set(k, {});
    const c = t.category || 'Miscellaneous';
    byMonthByCat.get(k)[c] = (byMonthByCat.get(k)[c] || 0) + Math.abs(Number(t.amount) || 0);
  });

  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const recaps = roll
    .filter((r) => r.key !== currentKey)
    .slice(-6)
    .reverse()
    .map((r, idx, arr) => {
      const prev = arr[idx + 1]; // previous month in reversed array (chronologically earlier)
      const surplus = r.income - r.spending;
      const prevSurplus = prev ? (prev.income - prev.spending) : null;
      const vs_last = prevSurplus != null && prevSurplus !== 0
        ? ((surplus - prevSurplus) / Math.abs(prevSurplus)) * 100
        : 0;

      // Score: single-month cash-flow-only preview (NOT the Clarity Score).
      // For the authoritative Clarity Score, callers should use lib/clarityScore.
      const cashFlow = r.income > 0 ? ((r.income - r.spending) / r.income) * 100 : 0;
      const score = Math.round(clamp01(((cashFlow + 20) / 60) * 100));

      // Highlights
      const highlights = [];
      if (surplus > 0) {
        highlights.push(`Kept ${_fmt(surplus, baseCurrency)} more than you spent this month.`);
      } else if (surplus < 0) {
        highlights.push(`Spent ${_fmt(-surplus, baseCurrency)} more than you earned this month.`);
      }
      if (prev) {
        if (r.spending > prev.spending * 1.05) {
          highlights.push(`Spending rose ${(((r.spending - prev.spending) / prev.spending) * 100).toFixed(1)}% compared to ${monthShortLabel(prev.key)}.`);
        } else if (r.spending < prev.spending * 0.95) {
          highlights.push(`Spending fell ${(((prev.spending - r.spending) / prev.spending) * 100).toFixed(1)}% compared to ${monthShortLabel(prev.key)}.`);
        }
      }
      // Top category
      const cats = byMonthByCat.get(r.key) || {};
      const topCat = Object.entries(cats).sort((a, b) => b[1] - a[1])[0];
      if (topCat) highlights.push(`Biggest spending category: ${topCat[0]} (${_fmt(topCat[1], baseCurrency)}).`);
      if (r.count >= 20) highlights.push(`${r.count} transactions recorded and categorised.`);
      if (highlights.length === 0) highlights.push('A quiet month — nothing stood out in your data.');

      const [y, m] = r.key.split('-');
      return {
        key: r.key,
        month: new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'long' }),
        year: Number(y),
        surplus,
        vs_last: Number(vs_last.toFixed(1)),
        score,
        headline:
          surplus > 0 && vs_last > 5     ? 'A stronger month than the one before.'
          : surplus > 0                   ? 'A steady positive month.'
          : surplus < 0 && vs_last < -5   ? 'Spending outpaced income more than usual.'
          : surplus < 0                   ? 'Spending edged past income this month.'
          : 'A balanced month.',
        highlights: highlights.slice(0, 4),
      };
    });

  return recaps;
}

// ─── 3. Milestones detector ──────────────────────────────────────────
export function detectMilestones({ txns = [], goals = [], baseCurrency = 'INR' }) {
  const out = [];
  const roll = rollupByMonth(txns);

  // Goal % milestones
  goals.forEach((g) => {
    const cur = Number(g.current_amount || 0);
    const tgt = Math.max(1, Number(g.target_amount || 1));
    const pct = (cur / tgt) * 100;
    const threshold = pct >= 100 ? 100 : pct >= 75 ? 75 : pct >= 50 ? 50 : pct >= 25 ? 25 : 0;
    if (threshold > 0) {
      out.push({
        id: `goal-${g.id}-${threshold}`,
        date: new Date(g.updated_at || Date.now()).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
        type: 'goal',
        color: '#3D7A63', bg: '#EEF4F0', label: 'Goal',
        headline: `${g.title || g.name || 'Your goal'} reached ${threshold}%`,
        detail: threshold === 100
          ? `You completed this goal. That's a real thing.`
          : `Consistent contribution is moving this goal forward on track.`,
        sortAt: new Date(g.updated_at || Date.now()).getTime(),
      });
    }
  });

  // Positive cash-flow streak milestone
  const streak = [...roll].reverse().findIndex((r) => (r.income - r.spending) <= 0);
  const consecutive = streak === -1 ? roll.length : streak;
  if (consecutive >= 3) {
    const lastKey = roll[roll.length - 1]?.key;
    out.push({
      id: `streak-${consecutive}`,
      date: lastKey ? monthShortLabel(lastKey) + ' ' + lastKey.split('-')[0] : '—',
      type: 'habit',
      color: '#163A32', bg: '#DFEEE5', label: 'Habit',
      headline: `${consecutive} consecutive months of positive cash flow`,
      detail: 'You have kept more than you spent every month in that window.',
      sortAt: Date.now() - 1000,
    });
  }

  // Tracking milestone
  const uniqueMonths = new Set(
    txns.map((t) => { const d = dateOf(t); return d ? monthKey(d) : null; }).filter(Boolean),
  ).size;
  if (uniqueMonths >= 3) {
    out.push({
      id: `track-${uniqueMonths}`,
      date: '—',
      type: 'habit',
      color: '#163A32', bg: '#DFEEE5', label: 'Habit',
      headline: `${uniqueMonths} months of consistent transaction tracking`,
      detail: 'Every month with recorded activity strengthens the pattern signal.',
      sortAt: Date.now() - 2000,
    });
  }

  // Category-trend milestone (biggest decrease)
  if (roll.length >= 6) {
    const last3 = roll.slice(-3);
    const prev3 = roll.slice(-6, -3);
    const bucket = (rows) => {
      const b = {};
      const keys = rows.map((r) => r.key);
      txns.filter((t) => {
        const d = dateOf(t);
        return t.type === 'expense' && d && keys.includes(monthKey(d));
      }).forEach((t) => {
        const c = t.category || 'Miscellaneous';
        b[c] = (b[c] || 0) + Math.abs(Number(t.amount) || 0);
      });
      return b;
    };
    const cur = bucket(last3); const prv = bucket(prev3);
    const diffs = Object.keys(cur)
      .map((c) => ({ c, cur: cur[c], prev: prv[c] || 0 }))
      .filter((d) => d.prev > 500 && d.cur < d.prev * 0.9);
    if (diffs.length > 0) {
      diffs.sort((a, b) => (b.prev - b.cur) - (a.prev - a.cur));
      const d = diffs[0];
      out.push({
        id: `insight-${d.c}`,
        date: monthShortLabel(roll[roll.length - 1].key) + ' ' + roll[roll.length - 1].key.split('-')[0],
        type: 'insight',
        color: '#3E6A80', bg: '#EEF2F5', label: 'Insight',
        headline: `${d.c} spend reduced meaningfully`,
        detail: `You spent ${_fmt(d.prev - d.cur, baseCurrency)} less on ${d.c} in the last quarter than the one before.`,
        sortAt: Date.now() - 3000,
      });
    }
  }

  // Sort newest first
  out.sort((a, b) => b.sortAt - a.sortAt);
  return out.slice(0, 8);
}
